import type { ReactNode } from 'react';
import {
  Badge,
  Card,
  MessageBar,
  MessageBarBody,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from '@fluentui/react-components';
import {
  AppsRegular,
  CloudRegular,
  DatabaseRegular,
  DocumentRegular,
  GlobeRegular,
  KeyRegular,
  LockClosedRegular,
  OrganizationRegular,
  PeopleTeamRegular,
  ShieldKeyholeRegular,
  ShieldRegular,
} from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { useSession } from '../auth/SessionProvider';
import './sitemap.css';

function Node({
  icon,
  title,
  sub,
  res,
  accent,
}: {
  icon: ReactNode;
  title: string;
  sub?: string;
  res?: string;
  accent?: boolean;
}) {
  return (
    <div className={`smap-node${accent ? ' accent' : ''}`}>
      <div className="smap-node-ic">{icon}</div>
      <div className="smap-node-body">
        <span className="smap-node-title">{title}</span>
        {sub ? <span className="smap-node-sub">{sub}</span> : null}
        {res ? <span className="smap-node-res">{res}</span> : null}
      </div>
    </div>
  );
}

function Flow({ label }: { label: string }) {
  return (
    <div className="smap-flow">
      <span className="smap-flow-label">{label}</span>
    </div>
  );
}

function Layer({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="smap-layer">
      <div className="smap-layer-title">
        {icon}
        {title}
      </div>
      <div className="smap-nodes">{children}</div>
    </div>
  );
}

// Microsoft-Graph-App-Rollen (Application/App-Only) der Managed Identity — Quelle:
// infra/grant-graph-permissions.ps1
const GRAPH_PERMS: { perm: string; use: string }[] = [
  { perm: 'User.ReadWrite.All', use: 'Benutzer anlegen/sperren/löschen, Attribute pflegen, Personensuche' },
  { perm: 'User.Invite.All', use: 'Gast-Einladungen (B2B) aussprechen' },
  { perm: 'Group.ReadWrite.All', use: 'Gruppen anlegen/löschen, Owner/Members' },
  { perm: 'GroupMember.ReadWrite.All', use: 'Mitglieder lesen/ändern (IAM, Software)' },
  { perm: 'TeamMember.ReadWrite.All', use: 'Team-Mitgliedschaften' },
  { perm: 'Directory.Read.All', use: 'Gruppenmitgliedschaft, Verzeichnisobjekte' },
  { perm: 'Organization.Read.All', use: 'Domains, Lizenz-Übersicht (subscribedSkus)' },
  { perm: 'Application.Read.All', use: 'Enterprise-Apps & App-Rollen (IAM)' },
  { perm: 'AppRoleAssignment.ReadWrite.All', use: 'App-Rollen-Zuweisungen (IAM)' },
  { perm: 'UserAuthenticationMethod.ReadWrite.All', use: 'Passwort-/MFA-Reset, TAP' },
  { perm: 'DeviceManagementManagedDevices.Read.All', use: 'Geräte-Inventar (Intune)' },
  { perm: 'DeviceLocalCredential.Read.All', use: 'LAPS (lokale Admin-Passwörter)' },
  { perm: 'DeviceManagementApps.Read.All', use: 'Intune-App-Inventar' },
  { perm: 'Policy.Read.All', use: 'Assessment: Conditional Access u. a.' },
  { perm: 'AuditLog.Read.All', use: 'Assessment: Anmelde-/Audit-Logs' },
  { perm: 'IdentityRiskyUser.Read.All', use: 'Assessment: riskante Benutzer' },
  { perm: 'SharePointTenantSettings.Read.All', use: 'Assessment: SPO-Einstellungen' },
  { perm: 'AccessReview.Read.All', use: 'Assessment: Access Reviews' },
  { perm: 'EntitlementManagement.Read.All', use: 'Assessment: Entitlement Mgmt' },
  { perm: 'Reports.Read.All', use: 'Assessment: Nutzungsberichte' },
];

// Kosten (Microsoft.CostManagement, Month-to-Date, EUR) + grobe Monatsschätzung.
const COSTS: { res: string; sku: string; mtd: string; est: string }[] = [
  { res: 'Container Apps (web + api)', sku: 'Consumption · min. 1 Replica · 0,75 vCPU / 1,5 GiB', mtd: 'im Free-Grant', est: '≈ 25–40 €' },
  { res: 'Azure SQL', sku: 'Serverless GP_S_Gen5_1 · Auto-Pause 60 min', mtd: '0,52 €', est: '≈ 8–15 €' },
  { res: 'Container Registry', sku: 'Basic', mtd: '0,12 €', est: '≈ 4,60 €' },
  { res: 'Log Analytics', sku: 'PerGB2018 · 30 Tage', mtd: '~ 0 €', est: '≈ 0–5 €' },
  { res: 'Key Vault', sku: 'Standard (RBAC)', mtd: '~ 0 €', est: '< 1 €' },
  { res: 'Managed Identity / Env / TLS-Cert', sku: 'inklusive', mtd: '0 €', est: '0 €' },
];

export function SiteMapPage() {
  const { me } = useSession();

  if (me && !me.isAdmin) {
    return (
      <div>
        <PageHeader title="Site-Map" />
        <MessageBar intent="warning">
          <MessageBarBody>Diese Architektur-Übersicht ist M365 Adminbox-Admins vorbehalten.</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <div className="smap">
      <PageHeader
        title="Site-Map"
        description="Architektur, Identität, Berechtigung und Betrieb von M365 Adminbox — für IT-Verantwortliche."
      />

      <MessageBar intent="info">
        <MessageBarBody>
          <b>Abonnement</b> SUB26-05 (gesponsert) · <b>Region</b> Germany West Central (DE-Datenresidenz) ·{' '}
          <b>Resource Group</b> rg-m365-adminbox · <b>Tenant</b> nxtwork.space
        </MessageBarBody>
      </MessageBar>

      {/* ---------- Architektur-Schaubild ---------- */}
      <div className="smap-arch">
        <Layer title="Client" icon={<GlobeRegular />}>
          <Node
            accent
            icon={<GlobeRegular />}
            title="Single-Page-App"
            sub="React 18 · Fluent UI v9 · MSAL · nginx — öffentlich über Custom-Domain + verwaltetes TLS"
            res="adminbox.nxtwork.space → m365adminbox-prod-web"
          />
        </Layer>

        <Flow label="① OAuth2 / OIDC — Auth Code + PKCE" />

        <Layer title="Identität — Microsoft Entra ID" icon={<ShieldKeyholeRegular />}>
          <Node icon={<KeyRegular />} title="App-Registrierung SPA" sub="Public Client (PKCE), Redirect auf die Domain" res="976df3a0-…" />
          <Node
            accent
            icon={<ShieldKeyholeRegular />}
            title="App-Registrierung API"
            sub="Token-Audience · App-Rollen Admin + Kunde (roles-Claim)"
            res="51b57c50-…"
          />
          <Node
            icon={<PeopleTeamRegular />}
            title="Zugriff über Gruppen → App-Rollen"
            sub="M365 Adminbox Admins → Admin · M365 Adminbox Kunden → Kunde"
            res="67d8c27b-… · b712e00a-…"
          />
        </Layer>

        <Flow label="② Bearer-Token (mit roles-Claim) an die API" />

        <Layer title="Compute — Azure Container Apps (Env m365adminbox-prod-env)" icon={<CloudRegular />}>
          <Node icon={<AppsRegular />} title="Frontend-Container" sub="Microsoft.App/containerApps · liefert die SPA" res="m365adminbox-prod-web" />
          <Node
            accent
            icon={<AppsRegular />}
            title="API-Container"
            sub=".NET 8 ASP.NET Core · validiert Token · erzwingt Rollen-/Funktionsrechte"
            res="m365adminbox-prod-api"
          />
          <Node
            icon={<ShieldRegular />}
            title="User-assigned Managed Identity"
            sub="Identität der App gegenüber Azure & Microsoft Graph (App-Only)"
            res="m365adminbox-prod-id"
          />
        </Layer>

        <Flow label="③ App-Only — Managed Identity (kein Benutzer-Kontext, keine Secrets im Code)" />

        <div className="smap-aside">
          <Layer title="Daten & Plattform" icon={<DatabaseRegular />}>
            <Node icon={<DatabaseRegular />} title="Azure SQL-Datenbank" sub="Serverless · App-Daten (Rollen, Governance, Audit, IAM)" res="m365adminbox-sql-…/m365adminbox" />
            <Node icon={<LockClosedRegular />} title="Key Vault" sub="RBAC · SQL-Connection-String als Secret" res="m365adminboxkvln7g2wju" />
            <Node icon={<AppsRegular />} title="Container Registry" sub="Basic · Container-Images (api/web)" res="m365adminboxacr…" />
            <Node icon={<DocumentRegular />} title="Log Analytics" sub="Container-Logs & Monitoring" res="m365adminbox-prod-logs" />
          </Layer>

          <Layer title="Microsoft 365 — Microsoft Graph" icon={<OrganizationRegular />}>
            <Node
              accent
              icon={<OrganizationRegular />}
              title="Microsoft Graph API"
              sub="Alle privilegierten Aktionen (Benutzer, Gruppen, Teams, Intune, IAM, Assessment) laufen App-Only über die Managed Identity — siehe Abschnitt Rechte der App."
            />
          </Layer>
        </div>
      </div>

      <div className="smap-legend">
        <span>① Anmeldung des Benutzers im Browser</span>
        <span>② Autorisierter API-Aufruf mit Rollen</span>
        <span>③ Privilegierte Ausführung App-Only</span>
      </div>

      {/* ---------- Berechtigung auf die App ---------- */}
      <Card>
        <div className="smap-section-title">Wie wird auf die App berechtigt?</div>
        <Text as="p" size={200}>
          Der Zugang ist <b>rollenbasiert</b> (Variante 2, Entra App Roles). Ein Benutzer wird — direkt oder über
          eine Gruppe — in der Enterprise Application <b>M365 Adminbox API</b> einer App-Rolle zugewiesen. Diese Rolle steht
          als <code>roles</code>-Claim im Zugriffstoken; die API liest sie ohne weiteren Graph-Aufruf.
        </Text>
        <ol style={{ margin: '4px 0 8px', paddingInlineStart: 18, fontSize: 13, lineHeight: 1.6 }}>
          <li>
            <b>Admin</b> (Gruppe „M365 Adminbox Admins" → App-Rolle <code>Admin</code>): Vollzugriff inkl. Einstellungen,
            Governance, Audit. Wird zusätzlich über <code>Authorization:AdminGroupId</code> akzeptiert (ODER-verknüpft).
          </li>
          <li>
            <b>Kunde</b> (Gruppe „M365 Adminbox Kunden" → App-Rolle <code>Kunde</code>): eingeschränkter Self-Service nach dem
            Prinzip <b>deny-by-default</b>. Welche Funktionen erlaubt sind, pflegt der Admin unter <b>Einstellungen → Rollen</b>.
          </li>
          <li>
            <b>Per-Nutzer-Sperren</b> (Einstellungen → Zugriffe) sowie Governance-Regeln (4-Augen, Tickets) wirken
            zusätzlich. Durchgesetzt zentral im API-Service <code>UserPermissionService</code>.
          </li>
        </ol>
        <div className="smap-chips">
          <Badge appearance="tint" color="brand">App-Rolle Admin · 2bbd54fb-…</Badge>
          <Badge appearance="tint" color="success">App-Rolle Kunde · 566502db-…</Badge>
          <Badge appearance="tint" color="informative">Enterprise App · M365 Adminbox API</Badge>
        </div>
      </Card>

      {/* ---------- Rechte der App ---------- */}
      <Card>
        <div className="smap-section-title">Welche Rechte hat die App?</div>
        <Text as="p" size={200}>
          M365 Adminbox handelt gegenüber Microsoft 365 <b>App-Only</b> über die Managed Identity <code>m365adminbox-prod-id</code> —
          es gibt keine im Code gespeicherten Secrets. Gegenüber Azure besitzt dieselbe Identität nur zwei eng gefasste
          RBAC-Rollen.
        </Text>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '8px 0' }}>
          <Badge appearance="outline" icon={<AppsRegular />}>Azure RBAC: AcrPull (Registry)</Badge>
          <Badge appearance="outline" icon={<LockClosedRegular />}>Azure RBAC: Key Vault Secrets User</Badge>
        </div>
        <Text size={200} weight="semibold" style={{ display: 'block', margin: '6px 0 4px' }}>
          Microsoft-Graph-Berechtigungen (Application / App-Only, per Admin-Consent):
        </Text>
        <Table size="extra-small" aria-label="Graph-Berechtigungen">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Berechtigung</TableHeaderCell>
              <TableHeaderCell>Wofür</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {GRAPH_PERMS.map((p) => (
              <TableRow key={p.perm}>
                <TableCell className="smap-perm">{p.perm}</TableCell>
                <TableCell>{p.use}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* ---------- Kosten ---------- */}
      <Card>
        <div className="smap-section-title">Laufende Kosten</div>
        <Text as="p" size={200}>
          Tatsächliche Kosten dieses Monats (Microsoft Cost Management, Month-to-Date) plus grobe Monatsschätzung bei
          Dauerbetrieb. Serverless/Consumption-SKUs skalieren mit der Nutzung; die App ist bewusst günstig ausgelegt.
        </Text>
        <Table size="small" aria-label="Kosten">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Ressource</TableHeaderCell>
              <TableHeaderCell>SKU / Konfiguration</TableHeaderCell>
              <TableHeaderCell>Ist (MTD)</TableHeaderCell>
              <TableHeaderCell>~ / Monat</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {COSTS.map((c) => (
              <TableRow key={c.res}>
                <TableCell>{c.res}</TableCell>
                <TableCell>{c.sku}</TableCell>
                <TableCell>{c.mtd}</TableCell>
                <TableCell>{c.est}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell><b>Summe (Schätzung)</b></TableCell>
              <TableCell />
              <TableCell />
              <TableCell><b>≈ 40–65 € / Monat</b></TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Text size={100} style={{ color: 'var(--colorNeutralForeground3)', display: 'block', marginTop: 6 }}>
          Hinweis: Container-Apps-Verbrauch erscheint mit Verzug bzw. liegt anfangs im monatlichen Free-Grant; die
          Monatswerte sind daher Richtwerte, keine Rechnung.
        </Text>
      </Card>

      {/* ---------- Betrieb / CI-CD ---------- */}
      <Card>
        <div className="smap-section-title">Auslieferung & Betrieb</div>
        <Text as="p" size={200}>
          Infrastruktur als Code (Bicep, <code>infra/main.bicep</code>). Deploy per <b>GitHub Actions</b> mit{' '}
          <b>OIDC</b> (föderierte Anmeldung, keine Secrets im Repo): Push auf <code>main</code> →{' '}
          <code>az acr build</code> (api/web) → Bicep-Rollout. EF-Core-Migrationen werden beim API-Start automatisch
          angewandt; Seeds (u. a. Standard-Rollenrechte) laufen idempotent mit.
        </Text>
      </Card>
    </div>
  );
}
